<template>
    <DCard padding>
        <DTable
            :items="items"
            :fields="fields"
            class="table-responsive"
            hover
        >
            <template #edit="{ item }">
                <td>
                    <DButton
                        :id="`edit_${item.id}`"
                        icon="pencil"
                        mini
                        @click="router.get(route('settings.bank-accounts.edit', { bankAccount: item.id }))"
                    />
                </td>
            </template>
            <template #delete="{ item }">
                <td>
                    <!-- v-c-tooltip renders an empty bubble for '' and never re-reads its value, so only the disabled button gets it. -->
                    <DButton
                        v-if="item.disabled_deletion === true"
                        :id="`btn-delete-${item.id}`"
                        v-c-tooltip="__('settings.bank_accounts.responses.error.assigned_to_market_region')"
                        icon="trash-can-outline"
                        color="danger"
                        mini
                        :disabled="true"
                    />
                    <DButton
                        v-else
                        :id="`btn-delete-${item.id}`"
                        icon="trash-can-outline"
                        color="danger"
                        mini
                        @click="confirmDelete(item.id)"
                    />
                </td>
            </template>
        </DTable>

        <DPagination
            v-if="bankAccounts.last_page > 1"
            :active-page="bankAccounts.current_page"
            :pages="bankAccounts.last_page"
            align="center"
            @update:activePage="changePage"
        />

        <DFabs>
            <DFab
                id="btn-add"
                icon="plus"
                :label="__('settings.bank_accounts.index.add_bank_account_button')"
                @click="router.get(route('settings.bank-accounts.create'))"
            />
        </DFabs>

        <DModal
            v-model:show="showDeleteConfirm"
            :title="__('settings.bank_accounts.index.delete_confirm_modal.title')"
            :message="__('settings.bank_accounts.index.delete_confirm_modal.message')"
            is-confirm
            is-error
            @close="showDeleteConfirm = false"
            @confirm="deleteBankAccount"
        />
    </DCard>
</template>

<script setup>
import { computed, ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    bankAccounts: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const COLUMNS = [
    'name',
    'sepa_iban',
    'sepa_bic',
    'sepa_bank',
    'sepa_comment',
    'transfer_iban',
    'transfer_bic',
    'transfer_bank',
    'transfer_account_number'
]

// The table is deliberately not sortable (no `sorter` on DTable).
const fields = [
    ...COLUMNS.map(key => ({ key, label: __(`settings.bank_accounts.index.table.headers.${key}`) })),
    { key: 'edit', label: '', filter: false },
    { key: 'delete', label: '', filter: false }
]

// The bank details are all optional; CSmartTable would render a missing one as "null".
const items = computed(() => (props.bankAccounts.data ?? []).map(bankAccount => ({
    ...bankAccount,
    ...Object.fromEntries(COLUMNS.map(key => [key, bankAccount[key] ?? '']))
})))

const showDeleteConfirm = ref(false)
const selectedId = ref(null)

function confirmDelete (id) {
    selectedId.value = id
    showDeleteConfirm.value = true
}

function deleteBankAccount () {
    router.delete(route('settings.bank-accounts.destroy', { bankAccount: selectedId.value }), {
        preserveScroll: true
    })
    showDeleteConfirm.value = false
}

function changePage (page) {
    const queryParameters = {}

    if (page > 1) {
        queryParameters.page = page
    }

    router.get(route('settings.bank-accounts.index'), queryParameters)
}
</script>
